import { FC, useState } from 'react';
import { InfoCard, Modal, Typography } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import { Stack } from '@mui/material';
import {
  AutoStoriesOutlined,
  CommentOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import {
  GenerateTrainingFromPromptModal,
  GenerateTrainingFromImportModal,
} from 'containers/surveys';

type Selection = 'material' | 'prompt' | 'import';

export const GenerateTrainingModal: FC<ModalProps> = ({ closeModal }) => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();

  const [selected, setSelected] = useState<Selection | null>(null);

  const handleContinue = () => {
    if (!selected) return;

    closeModal();

    if (selected === 'import') {
      openModal({
        component: GenerateTrainingFromImportModal,
        size: 'lg',
      });
    } else {
      openModal({
        component: GenerateTrainingFromPromptModal,
        props: { type: selected },
        size: 'lg',
      });
    }
  };

  return (
    <Modal.Content
      title={t('trainings.actions.create')}
      actions={[
        {
          variant: 'neutral-outline',
          onClick: () => closeModal(),
          label: t('common.cancel'),
        },
        {
          variant: 'primary',
          label: t('common.continue'),
          onClick: handleContinue,
          disabled: !selected,
        },
      ]}
    >
      <Stack gap={3}>
        <Typography variant="p1" color="textSecondary">
          {t('trainings.generate.description')}
        </Typography>

        <Stack
          direction="row"
          alignItems="stretch"
          sx={{ '& > *': { flex: 1 } }}
          gap={2}
        >
          <InfoCard
            icon={AutoStoriesOutlined}
            title={t('trainings.generate.material.title')}
            text={t('trainings.generate.material.description')}
            direction="column"
            variant={selected === 'material' ? 'info' : 'neutral'}
            onClick={() => setSelected('material')}
          />

          <InfoCard
            icon={CommentOutlined}
            title={t('trainings.generate.prompt.title')}
            text={t('trainings.generate.prompt.description')}
            direction="column"
            variant={selected === 'prompt' ? 'info' : 'neutral'}
            onClick={() => setSelected('prompt')}
          />

          <InfoCard
            icon={UploadFileOutlined}
            title={t('trainings.generate.import.title')}
            text={t('trainings.generate.import.description')}
            direction="column"
            variant={selected === 'import' ? 'info' : 'neutral'}
            onClick={() => setSelected('import')}
          />
        </Stack>
      </Stack>
    </Modal.Content>
  );
};
